import React, { useCallback, useEffect, useRef, useState } from "react";

import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import {
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel
} from "@mui/material";
import { FileViewer } from "components/popups";
import { useDidUpdate, useFormatDateHook, useModals } from "hooks";
import { useReportsByGroups } from "hooks";
import { throttle } from "lodash";
import {
  IApiServiceError,
  useCreateManyMeasureDataSelect,
  useDealActions,
  useMeasureParametersSelector,
  useMonitoringDataFilters
} from "store";
import {
  CurrencyType,
  GetSplitDetailsReq,
  IGetMonitoringData,
  MeasureDataSplitDetails,
  MeasureDataTableOrderColumn,
  MeasureDataTableRow,
  Order
} from "store/types";

import { currencySign, FileType } from "../../../../constants";
import { EditMany } from "../../Edit-many/EditMany";
import { UpdateOne } from "../../Update-one/UpdateOne";
import { Row } from "../Row/Row";

const RESERVED_HEIGH = 150;
const REPORT_GROUPS = ["measure_data"];

const cellCommonStyles: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "15px",
  fontWeight: "normal",
  border: "none",
  whiteSpace: "nowrap"
};

interface MonitoringTableProps {
  showTopInvestors: boolean;
  setLoading: (value: boolean) => void;
  setEmpty?: (value: boolean) => void;
  dealId?: number;
  onlyDeleted?: boolean;
}

const sortableColumnKeys: MeasureDataTableOrderColumn[] = [
  "deal",
  "parameter",
  "round",
  "date",
  "accounting_date",
  "effective_range_from",
  "effective_range_to",
  "entered_by"
];

export const DataTable: React.FC<MonitoringTableProps> = ({
  showTopInvestors,
  setLoading,
  setEmpty,
  dealId,
  onlyDeleted = false
}) => {
  const filters = useMonitoringDataFilters();
  const createManyMeasure = useCreateManyMeasureDataSelect();
  const measureParameters = useMeasureParametersSelector();

  const [itemId, setItemId] = useState<number | null>(null);
  const [columns, setColumns] = useState<{ title: string; key: keyof MeasureDataTableRow }[]>([]);
  const [rows, setRows] = useState<MeasureDataTableRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [allOpen, setAllOpen] = useState<boolean>(false);
  const [pdfForViewer, setPdfForViewer] = useState<{ file_url: string; type: FileType; name: string } | null>(null);

  const [editSplits, setEditSplits] = useState<{
    splitDetails: MeasureDataSplitDetails;
    defaultCurrency: CurrencyType;
    deal_id: number;
    batch_id?: number;
  } | null>(null);
  const [sorting, setSorting] = useState<{ order: Order | null; orderBy: MeasureDataTableOrderColumn | null }>({
    order: null,
    orderBy: null
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const { getMeasureDataTableAction, getSplitDetailsAction, deleteMeasureDataAction, getDealAction } = useDealActions();
  const formatDate = useFormatDateHook();
  const { deleteModal, errorModal } = useModals();
  const { getReportsByGroup } = useReportsByGroups(REPORT_GROUPS);

  const handleSort = (columnKey: MeasureDataTableOrderColumn) => {
    const nextOrderSteps: Record<Order, Order | null> = {
      asc: "desc",
      desc: null
    };
    setSorting((prevState) => {
      if (prevState.orderBy && prevState.orderBy !== columnKey) {
        return { order: "asc", orderBy: columnKey };
      }

      const resultOrder = !prevState.order ? "asc" : nextOrderSteps[prevState.order];
      return { order: resultOrder, orderBy: resultOrder ? columnKey : null };
    });
  };

  const inspectGroup = useCallback(
    async (data: GetSplitDetailsReq): Promise<void> => {
      try {
        const res = await getSplitDetailsAction(data);
        const { currency } = await getDealAction(data.deal_id);

        const currencies = currency?.data || [];

        setEditSplits({
          splitDetails: res,
          deal_id: data.deal_id,
          defaultCurrency: currencies.find((curr) => curr.is_default)?.value as CurrencyType,
          batch_id: data.batch_id
        });
      } catch (err) {
        errorModal({ msg: (err as IApiServiceError)?.message });
      }
    },
    [dealId, measureParameters]
  );

  const normalizeRows = useCallback(
    (rows: MeasureDataTableRow[]): MeasureDataTableRow[] => {
      return rows.map((item) => {
        if (!item.accounting_date && item.children?.length) {
          const childWithDate = item.children.find((child) => child.accounting_date);
          if (childWithDate) {
            item.accounting_date = childWithDate.accounting_date;
          }
        }
        if (item.date) {
          item.date = formatDate({ date: item.date });
        }
        if (item.accounting_date) {
          item.accounting_date = formatDate({ date: item.accounting_date });
        }
        if (item.effective_range_from) {
          item.effective_range_from = formatDate({ date: item.effective_range_from });
        }
        if (item.effective_range_to) {
          item.effective_range_to = formatDate({ date: item.effective_range_to });
        }
        if (item.updated_at) {
          item.updated_at = formatDate({ date: item.updated_at });
        }
        if (item.value) {
          const numerified = Number(item.value)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          const postfix = item.is_currency ? currencySign[item.currency!] : item.postfix || "";
          item.value = item.postfix === "%" ? `${numerified}%` : `${postfix}${numerified}`;
        }
        if (item.children) {
          item.children = normalizeRows(item.children);
        }
        return item;
      });
    },
    [formatDate]
  );

  const getData = useCallback(
    async ({
      page = 1,
      filters,
      showTopInvestors,
      sort,
      dropPreviousState = false
    }: IGetMonitoringData & { dropPreviousState?: boolean }) => {
      try {
        setIsLoading(true);
        const {
          columns,
          rows: rowsResponse,
          last_page,
          current_page
        } = await getMeasureDataTableAction({
          page,
          filters: dealId ? { ...filters, deals: [{ title: "", id: Number(dealId) }] } : filters,
          showTopInvestors,
          is_general: Boolean(!dealId),
          onlyDeleted,
          sort
        });

        const transformedRows = normalizeRows(rowsResponse);

        setColumns(columns);
        setRows((prevState) => [...(dropPreviousState ? [] : prevState), ...transformedRows]);
        if (Number(current_page) >= last_page) {
          setHasMore(false);
        } else {
          setPage(page + 1);
        }
      } catch (err) {
        console.log(err);
      } finally {
        processing = false;
        setIsLoading(false);
      }
    },
    [hasMore, dealId, normalizeRows, onlyDeleted]
  );

  const resetData = () => {
    setPage(1);
    setColumns([]);
    setRows([]);
    setHasMore(true);
  };

  const refetch = useCallback(() => {
    resetData();
    getData({ page: 1, filters, showTopInvestors, sort: sorting, dropPreviousState: true });
  }, [filters, showTopInvestors, sorting]);

  const getNext = useCallback(() => {
    getData({ page, filters, showTopInvestors, sort: sorting });
  }, [page, filters, showTopInvestors, sorting]);

  // Initial call
  useEffect(() => {
    getData({ page: 1, filters, showTopInvestors, sort: sorting });
  }, []);

  useDidUpdate(() => {
    if (isLoading) return;

    // Skip if these modals are open
    if (createManyMeasure || editSplits || itemId) return;

    resetData();
    getData({ page: 1, filters, showTopInvestors, sort: sorting, dropPreviousState: true });
  }, [filters, showTopInvestors, createManyMeasure, editSplits, itemId, sorting, onlyDeleted]);

  let processing = false;

  const onScroll = useCallback(
    (e: Event) => {
      if (processing) return;
      if (isLoading) return;
      if (!hasMore) return;

      const { scrollHeight, clientHeight, scrollTop } = e.target as HTMLDivElement;
      const shouldGetNext: boolean = scrollHeight - clientHeight - scrollTop - RESERVED_HEIGH <= 0;

      if (shouldGetNext) {
        processing = true;
        getNext();
      }
    },
    [processing, isLoading, hasMore, getNext]
  );

  const throttleOnScroll = useCallback(throttle(onScroll, 200), [onScroll]);

  const hasActiveFilters =
    !!filters.deals?.length ||
    filters.accountingDate !== null ||
    filters.effectiveDate !== null ||
    filters.createdAtDate !== null ||
    filters.enteredByDate !== null ||
    !!filters.measures?.length ||
    !!filters.investors?.length ||
    !!filters.enteredBy?.length ||
    !!filters.calculatedFields?.length ||
    !!filters.lastTransactions;

  useEffect(() => {
    const { current } = containerRef;

    if (current) {
      current.addEventListener("scroll", throttleOnScroll);
    }

    return () => current?.removeEventListener("scroll", throttleOnScroll);
  }, [throttleOnScroll]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      setEmpty?.(!rows.length && !hasActiveFilters);
    }
  }, [isLoading, rows, hasActiveFilters]);

  const deleteRow = useCallback(
    async (id: number, isParent = false) => {
      if (isParent) {
        deleteModal({
          action: async () => {
            await deleteMeasureDataAction(id, true);
            refetch();
          },
          element: "Delete",
          message:
            "This will delete the entire transaction with all the related lines per different investors. Are you sure you want to delete data?"
        });
        return;
      }
      deleteModal({
        action: async () => {
          await deleteMeasureDataAction(id);
          refetch();
        },
        element: "Delete",
        message: "Are you sure you want to delete data?",
        onPressSuccess: "Delete"
      });
    },
    [refetch]
  );

  const toggleAllRows = () => {
    setAllOpen(!allOpen); // Toggle the state for all rows
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          border: "0.5px solid #dae0e2",
          flex: 1,
          minHeight: 0
        }}
        ref={containerRef}
      >
        <Table
          sx={{
            ".MuiTableCell-root": {
              padding: "8px 26px",
              ":last-child": { width: "4%" },
              ":first-child": { width: "1%", padding: "8px 0px 8px 12px" }
            }
          }}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell sx={cellCommonStyles}>
                <IconButton size="sm" onClick={toggleAllRows}>
                  {allOpen ? <KeyboardDoubleArrowUpIcon /> : <KeyboardDoubleArrowDownIcon />}
                </IconButton>
              </TableCell>
              {columns.map(({ title, key }, index: number) => (
                <TableCell sx={cellCommonStyles} align={!index ? "left" : "center"} key={key}>
                  {sortableColumnKeys.includes(key as MeasureDataTableOrderColumn) ? (
                    <TableSortLabel
                      key={key}
                      disabled={isLoading}
                      onClick={() => handleSort(key as MeasureDataTableOrderColumn)}
                      {...(sorting.orderBy === key && sorting.order && { direction: sorting.order })}
                      active={sorting.orderBy === key}
                    >
                      {title}
                    </TableSortLabel>
                  ) : (
                    title
                  )}
                </TableCell>
              ))}
              <TableCell sx={cellCommonStyles} />
              <TableCell sx={cellCommonStyles} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row, index) => (
              <Row
                key={row.id || index}
                dealId={Number(dealId)}
                row={row}
                columns={columns.map((item) => item.key)}
                deleteRow={deleteRow}
                inspectRow={setItemId}
                inspectGroup={inspectGroup}
                isOpen={allOpen}
                onFileClick={setPdfForViewer}
                getReportsByGroup={getReportsByGroup}
              />
            ))}
          </TableBody>
          {columns && isLoading && (
            <TableFooter>
              <TableRow sx={{ ".MuiTableCell-root": { ":first-child": { p: 0 } } }}>
                <TableCell sx={{ padding: "none" }} colSpan={columns.length + 3}>
                  <Skeleton sx={{ bgcolor: "#b2bec3", height: "60px", width: "100%" }} variant="rectangular" />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
        <UpdateOne id={itemId} close={() => setItemId(null)} />
      </TableContainer>
      <EditMany {...editSplits} handleClose={() => setEditSplits(null)} />
      {pdfForViewer?.file_url && (
        <FileViewer
          file={pdfForViewer}
          isOpen={Boolean(pdfForViewer)}
          onClose={() => {
            setPdfForViewer(null);
          }}
        />
      )}
    </>
  );
};
